import { useCallback, useEffect, useRef, useState } from 'react';
import pasteParser from 'components/FeaturedSpreadsheet/utilities/pasteParser';
import { ICell } from 'models/interfaces/spreadsheet-types/core-types';
import ScalarDatasheetProps, { Selection } from './ScalarDatasheet.props';
import CellCountConfig from '../../models/interfaces/cell-count-config.interface';

interface UseDatasheetClipboardPasteParams {
  data: ICell[][];
  onCellsChanged: ScalarDatasheetProps['onCellsChanged'];
  clearSelectedFlag: boolean;
  setClearSelected: (value: boolean) => void;
  enableCopyFeedback?: boolean;
}

const convertPastedActualToCellInput = (cell: ICell | undefined, value: unknown) => {
  const inputScale = cell?.format?.inputScale ?? 1;
  const numericValue = Number(value);
  if (
    value === '' ||
    !Number.isFinite(numericValue) ||
    !Number.isFinite(inputScale) ||
    inputScale <= 0 ||
    inputScale === 1
  ) {
    return value;
  }

  // Clipboard numbers are canonical actuals. Convert them into the same input
  // domain as the editor so the owning sheet applies its unit scale exactly once.
  return numericValue / inputScale;
};

const convertPastedActualsForCells = (
  parsedClipboard: any[][],
  data: ICell[][],
  startI: number,
  startJ: number
) =>
  parsedClipboard.map((row, rowOffset) =>
    (row ?? []).map((value: unknown, colOffset: number) =>
      convertPastedActualToCellInput(data?.[startI + rowOffset]?.[startJ + colOffset], value)
    )
  );

const useDatasheetClipboardPaste = ({
  data,
  onCellsChanged,
  clearSelectedFlag,
  setClearSelected,
  enableCopyFeedback = false,
}: UseDatasheetClipboardPasteParams) => {
  const [maxAllowedCells, setMaxAllowedCells] = useState<CellCountConfig>({});
  const [selected, setSelected] = useState<Selection | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const copyFeedbackTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const selectedRef = useRef<Selection | null>(selected);
  const dataRef = useRef(data);
  const onCellsChangedRef = useRef(onCellsChanged);

  useEffect(() => {
    if (clearSelectedFlag) {
      setSelected(null);
      setClearSelected(false);
    }
  }, [clearSelectedFlag, setClearSelected]);

  useEffect(() => {
    selectedRef.current = selected;
  }, [selected]);

  useEffect(() => {
    dataRef.current = data;
  }, [data]);

  useEffect(() => {
    onCellsChangedRef.current = onCellsChanged;
  }, [onCellsChanged]);

  // Flash selected cells after copy so readonly spreadsheet previews confirm clipboard updates.
  useEffect(() => {
    if (!enableCopyFeedback) {
      return undefined;
    }

    const handleCopy = () => {
      const containerEl = containerRef.current;
      if (!containerEl || !selectedRef.current) {
        return;
      }

      const selectedCells = containerEl.querySelectorAll<HTMLElement>('td.selected');
      if (!selectedCells.length) {
        return;
      }

      if (copyFeedbackTimeoutRef.current) {
        clearTimeout(copyFeedbackTimeoutRef.current);
        copyFeedbackTimeoutRef.current = null;
      }
      const ownerWindow = containerEl.ownerDocument.defaultView ?? window;
      selectedCells.forEach(cell => {
        cell.classList.remove('cell-copy-feedback');
      });
      ownerWindow.requestAnimationFrame(() => {
        selectedCells.forEach(cell => cell.classList.add('cell-copy-feedback'));
      });

      copyFeedbackTimeoutRef.current = setTimeout(() => {
        selectedCells.forEach(cell => cell.classList.remove('cell-copy-feedback'));
      }, 950);
    };

    document.addEventListener('copy', handleCopy, true);
    return () => {
      document.removeEventListener('copy', handleCopy, true);
      if (copyFeedbackTimeoutRef.current) {
        clearTimeout(copyFeedbackTimeoutRef.current);
        copyFeedbackTimeoutRef.current = null;
      }
    };
  }, [enableCopyFeedback]);

  const getClipboardText = (e: ClipboardEvent): string => {
    const direct = e.clipboardData?.getData('text/plain') ?? '';
    if (direct) {
      return direct;
    }

    // Legacy clipboard fallback for environments that still expose clipboardData.
    const legacyClipboardData = (globalThis as any).clipboardData;
    return legacyClipboardData ? legacyClipboardData.getData('Text') : '';
  };

  const getSelectionBounds = (sel: Selection) => ({
    startI: Math.min(sel.start.i, sel.end.i),
    endI: Math.max(sel.start.i, sel.end.i),
    startJ: Math.min(sel.start.j, sel.end.j),
    endJ: Math.max(sel.start.j, sel.end.j),
  });

  const applyPasteToSelection = useCallback((parsedClipboard: any[][], startI: number, startJ: number) => {
    const currentOnCellsChanged = onCellsChangedRef.current;
    if (!currentOnCellsChanged) {
      return;
    }

    // Build the exact change/addition payload shape expected by react-datasheet.
    const changes: any[] = [];
    const additions: any[] = [];
    for (let i = 0; i < parsedClipboard.length; i += 1) {
      const row = parsedClipboard[i] ?? [];
      for (let j = 0; j < row.length; j += 1) {
        const rowIndex = startI + i;
        const colIndex = startJ + j;
        const cell = dataRef.current?.[rowIndex]?.[colIndex];
        const value = convertPastedActualToCellInput(cell, row[j]);

        if (!cell) {
          additions.push({ row: rowIndex, col: colIndex, value });
        } else if (!cell.readOnly) {
          changes.push({ cell, row: rowIndex, col: colIndex, value });
        }
      }
    }

    if (additions.length) {
      currentOnCellsChanged(changes as any, additions as any);
    } else {
      currentOnCellsChanged(changes as any);
    }
  }, []);

  useEffect(() => {
    const handlePasteWhileEditing = (e: ClipboardEvent) => {
      const containerEl = containerRef.current;
      if (!containerEl) {
        return;
      }

      const activeEl = document.activeElement as HTMLElement | null;
      if (!activeEl || !containerEl.contains(activeEl)) {
        return;
      }
      if (!['INPUT', 'TEXTAREA'].includes(activeEl.tagName)) {
        return;
      }

      const currentSelected = selectedRef.current;
      if (!currentSelected) {
        return;
      }

      const clipboardText = getClipboardText(e);
      if (!clipboardText) {
        return;
      }

      const { startI, endI, startJ, endJ } = getSelectionBounds(currentSelected);

      const maxAllowedRows = endI - startI + 1;
      const maxAllowedColumns = endJ - startJ + 1;
      const selectionCellsCount = maxAllowedRows * maxAllowedColumns;

      // When an editor input is focused, @scalartech/react-datasheet does not fan out
      // a multi-cell paste. We intercept here and apply the grid paste ourselves.
      const parsedClipboard =
        // Single-cell selection means "paste from this origin", so we keep clipboard
        // dimensions (e.g. 2x3) instead of constraining to 1x1.
        selectionCellsCount <= 1
          ? (pasteParser(clipboardText) as any[][])
          : (pasteParser(clipboardText, { maxAllowedRows, maxAllowedColumns }) as any[][]);

      applyPasteToSelection(parsedClipboard, startI, startJ);

      // The grid paste above is authoritative. Prevent the browser from also
      // inserting the clipboard text into the focused editor input.
      e.preventDefault();
      e.stopPropagation();
    };

    document.addEventListener('paste', handlePasteWhileEditing, true);
    return () => document.removeEventListener('paste', handlePasteWhileEditing, true);
  }, [applyPasteToSelection]);

  const handleOnPaste = useCallback(
    (clipboard: string) => {
      const { maxAllowedRows, maxAllowedColumns } = maxAllowedCells;
      const shouldUseSelectionBounds =
        Boolean(maxAllowedRows && maxAllowedColumns) && Number(maxAllowedRows) * Number(maxAllowedColumns) > 1;
      const config = shouldUseSelectionBounds
        ? { maxAllowedRows: maxAllowedRows!, maxAllowedColumns: maxAllowedColumns! }
        : undefined;
      const parsedClipboard = pasteParser(clipboard, config) as any[][];
      const currentSelected = selectedRef.current;
      if (!currentSelected) {
        return parsedClipboard as string[][];
      }

      const { startI, startJ } = getSelectionBounds(currentSelected);
      return convertPastedActualsForCells(parsedClipboard, dataRef.current, startI, startJ) as string[][];
    },
    [maxAllowedCells]
  );

  return {
    selected,
    setSelected,
    setMaxAllowedCells,
    containerRef,
    handleOnPaste,
  };
};

export default useDatasheetClipboardPaste;
